import { contentLanguage } from '../i18n/supported'
import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { supplementsAPI } from '../services/api'
import { PrinterIcon, SparklesIcon } from '@heroicons/react/24/outline'
import TimingGroupSection from '../components/supplements/TimingGroupSection'

// Deterministic section order for the grouped report. Fluid dosage forms (drops, spray)
// render first, regardless of their time-of-day — they're taken separately throughout the day.
const GROUP_ORDER = ['fluid', 'morning', 'morning_evening', 'evening', 'unscheduled']
const FLUID_FORMS = new Set(['liquid', 'spray'])
const isFluidForm = (form) => FLUID_FORMS.has((form || '').toString().toLowerCase())

/**
 * VH-81: Daily Supplement Report with timing groups + AI purpose/benefits summaries.
 *
 * Renders four distinct sections (Morning / Morning & Evening / Evening / Unscheduled)
 * with a Notes column that either shows a cached AI summary or a click-to-generate CTA.
 * No AI call happens at page load.
 */
export default function DailySupplementReport() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const today = new Date().toISOString().split('T')[0]

  // Bulk-generate-all-missing state (VH-81 feedback fix)
  const [bulkProgress, setBulkProgress] = useState(null) // { done, total } | null
  const [bulkError, setBulkError] = useState('')

  // Normalize language (e.g. "de-DE" -> "de", keep "pt-BR")
  const currentLanguage = useMemo(() => {
    return contentLanguage(i18n.language)
  }, [i18n.language])

  const { data: userSupplements, isLoading, error } = useQuery({
    queryKey: ['user-supplements', currentLanguage],
    queryFn: async () => {
      // Backend may accept ?language=… to pre-populate purpose_summary; harmless if ignored.
      const response = await supplementsAPI.getUserSupplements()
      const allSupplements = response.data?.user_supplements || []
      return Array.isArray(allSupplements) ? allSupplements.filter(s => s.is_active) : []
    },
  })

  const getLocale = () => {
    const lang = i18n.language || 'en'
    const localeMap = {
      en: 'en-US',
      de: 'de-DE',
      'pt-BR': 'pt-BR',
      pt: 'pt-BR',
      fr: 'fr-FR',
      es: 'es-ES',
      it: 'it-IT',
    }
    return localeMap[lang] || localeMap[lang.split('-')[0]] || 'en-US'
  }

  const translateUnit = (unit) => {
    const unitKey = `supplements.units.${unit}Lower`
    const translated = t(unitKey)
    return translated !== unitKey ? translated : unit
  }

  const translateForm = (form) => {
    if (!form) return ''
    const formKey = `supplements.forms.${form}`
    const translated = t(formKey)
    return translated !== formKey ? translated : form
  }

  /**
   * Decide which timing group a user_supplement belongs to per the spec rules.
   * Server may already set `group`; otherwise compute client-side as fallback.
   */
  const resolveGroup = (us) => {
    if (us.group && GROUP_ORDER.includes(us.group)) return us.group
    const m = us.morning_quantity || 0
    const e = us.evening_quantity || 0
    if (m > 0 && e > 0) return 'morning_evening'
    if (m > 0) return 'morning'
    if (e > 0) return 'evening'
    // Fall back to legacy time_of_day enum
    const tod = (us.time_of_day || '').toString().toLowerCase()
    if (tod === 'morning') return 'morning'
    if (tod === 'evening') return 'evening'
    if (tod === 'morning_evening' || tod === 'both') return 'morning_evening'
    return 'unscheduled'
  }

  /**
   * Collapse user supplements into display rows, one per supplement-name, summing doses.
   * Preserve supplement id (for the purpose-summary endpoint) and its group.
   */
  const groupedRows = useMemo(() => {
    const result = { fluid: [], morning: [], morning_evening: [], evening: [], unscheduled: [] }
    if (!userSupplements) return result

    // Map keyed by supplement name so multiple user-supplement rows of the same product merge.
    const byName = new Map()

    userSupplements.forEach(us => {
      if (!us.is_active) return
      const supplement = us.supplement
      if (!supplement) return

      const key = supplement.name
      if (!byName.has(key)) {
        byName.set(key, {
          supplementId: supplement.id,
          name: supplement.name,
          brand: supplement.brand,
          form: supplement.dosage_form,
          unit: us.quantity_unit || 'serving',
          morning: 0,
          midday: 0,
          evening: 0,
          group: resolveGroup(us),
          // Spec US-2: purpose_summary provided by server when cached; null otherwise.
          purposeSummary: us.purpose_summary || null,
        })
      }
      const entry = byName.get(key)
      entry.morning += us.morning_quantity || 0
      entry.midday += us.midday_quantity || 0
      entry.evening += us.evening_quantity || 0
      // If any instance has a cached summary, prefer it.
      if (!entry.purposeSummary && us.purpose_summary) {
        entry.purposeSummary = us.purpose_summary
      }
    })

    // Sort rows within each group alphabetically for stable render.
    Array.from(byName.values())
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach(row => {
        // Fluid dosage forms (drops, spray) always go into the Fluid section,
        // overriding the time-of-day grouping.
        const group = isFluidForm(row.form)
          ? 'fluid'
          : (result[row.group] ? row.group : 'unscheduled')
        result[group].push(row)
      })

    return result
  }, [userSupplements])

  const handlePrint = () => {
    window.print()
  }

  // Count supplements that have no cached summary for the current language.
  const missingSummaryIds = useMemo(() => {
    const ids = []
    GROUP_ORDER.forEach((g) => {
      (groupedRows[g] || []).forEach((row) => {
        if (!row.purposeSummary && row.supplementId) ids.push(row.supplementId)
      })
    })
    return ids
  }, [groupedRows])

  const handleGenerateAllMissing = async () => {
    if (missingSummaryIds.length === 0 || bulkProgress) return
    setBulkError('')
    const total = missingSummaryIds.length
    setBulkProgress({ done: 0, total })
    let failed = 0
    for (let i = 0; i < missingSummaryIds.length; i++) {
      const id = missingSummaryIds[i]
      try {
        await supplementsAPI.generatePurposeSummary(id, currentLanguage)
      } catch (err) {
        failed += 1
        // continue; don't block the whole batch on a single failure
      }
      setBulkProgress({ done: i + 1, total })
    }
    // Refetch so rows pick up the new cached summaries
    await queryClient.invalidateQueries({ queryKey: ['user-supplements'] })
    setBulkProgress(null)
    if (failed > 0) setBulkError(t('report.bulkGenerateSomeFailed', { failed, total }))
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <p className="text-red-800 text-lg font-semibold">Error loading supplements</p>
          <p className="text-red-600 mt-2">{error.message}</p>
        </div>
      </div>
    )
  }

  const totalSupplements = GROUP_ORDER.reduce((sum, g) => sum + groupedRows[g].length, 0)
  const totalDoses = GROUP_ORDER.reduce(
    (sum, g) => sum + groupedRows[g].reduce((s, r) => s + r.morning + r.midday + r.evening, 0),
    0
  )
  const morningCount = (groupedRows.morning.length + groupedRows.morning_evening.length)
  const eveningCount = (groupedRows.evening.length + groupedRows.morning_evening.length)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header - Hidden when printing */}
      <div className="mb-6 print:hidden">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('report.title')}</h1>
            <p className="mt-2 text-gray-600">
              {new Date(today).toLocaleDateString(getLocale(), {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(missingSummaryIds.length > 0 || bulkProgress) && (
              <button
                onClick={handleGenerateAllMissing}
                disabled={!!bulkProgress || missingSummaryIds.length === 0}
                className="btn-secondary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                title={t('report.bulkGenerateHint')}
              >
                <SparklesIcon className="h-5 w-5" />
                {bulkProgress
                  ? t('report.bulkGenerateProgress', { done: bulkProgress.done, total: bulkProgress.total })
                  : t('report.bulkGenerate', { count: missingSummaryIds.length })}
              </button>
            )}
            <button
              onClick={handlePrint}
              className="btn-primary flex items-center gap-2"
            >
              <PrinterIcon className="h-5 w-5" />
              {t('report.print')}
            </button>
          </div>
        </div>
        {bulkError && (
          <p className="mt-2 text-sm text-red-600">{bulkError}</p>
        )}
      </div>

      {/* Compact print header */}
      <div className="hidden print:block print:mb-1">
        <h1 className="text-sm font-bold text-gray-900">
          {t('report.title')} -{' '}
          {new Date(today).toLocaleDateString(getLocale(), {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </h1>
      </div>

      {/* Summary card - hidden when printing */}
      {totalSupplements > 0 && (
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 mb-6 text-white print:hidden">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-3xl font-bold">{totalSupplements}</div>
              <div className="text-sm opacity-90">{t('report.supplements')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold">{totalDoses}</div>
              <div className="text-sm opacity-90">{t('report.totalDoses')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold">{morningCount}</div>
              <div className="text-sm opacity-90">{t('report.morning')}</div>
            </div>
            <div>
              <div className="text-3xl font-bold">{eveningCount}</div>
              <div className="text-sm opacity-90">{t('report.evening')}</div>
            </div>
          </div>
        </div>
      )}

      {totalSupplements === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <p className="text-yellow-800 text-lg">{t('report.noSupplements')}</p>
          <p className="text-yellow-600 mt-2">{t('report.addSupplements')}</p>
        </div>
      ) : (
        <div>
          {GROUP_ORDER.map(groupKey => (
            <TimingGroupSection
              key={groupKey}
              groupKey={groupKey}
              rows={groupedRows[groupKey]}
              language={currentLanguage}
              translateUnit={translateUnit}
              translateForm={translateForm}
            />
          ))}
          {/* Disclaimer footer - translatable */}
          <p className="mt-6 text-xs text-gray-500 italic print:text-[8px] print:mt-2">
            {t('report.disclaimer')}
          </p>
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 1cm;
            size: A4 portrait;
          }
          * { box-sizing: border-box; }
          .p-6 {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
          }
          table {
            font-size: 10px !important;
            page-break-inside: auto;
            margin-top: 0 !important;
            border-collapse: collapse !important;
          }
          .rounded-lg, .shadow-md {
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          td .font-semibold { font-size: 10px !important; }
          td .text-lg { font-size: 11px !important; }
          td .text-sm { font-size: 8px !important; line-height: 1.2 !important; }
          th { font-size: 9px !important; }
          tr { page-break-inside: avoid; page-break-after: auto; }
          thead { display: table-header-group; }
          tfoot { display: table-footer-group; }
          section {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  )
}
